import { LabProcessingStatusFilterOption } from "@/Common/Models/Lab/Enum/LabProcessingStatusFilterOption";
import { PortalStatusCheckable } from "@/Common/Models/Portal/PortalStatusCheckable";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { LabSettings } from "@/Labs/Models/Settings/LabSettings";
import { LabsStore } from "@/Labs/Stores/LabsStore";

class LabsInboxSettingsContainerHandler extends BaseOperationHandler {
    public constructor() {
        super();

        this.RegisterOperation(LocalOperations.Labs.LabInboxSearchSettings_ClinicianFilterTextChanged, this.ClinicianFilterChanged);
        this.RegisterOperation(LocalOperations.Labs.LabInboxSearchSettings_SentByTextChanged, this.SentByFilterChanged);
        this.RegisterOperation(LocalOperations.Labs.LabInboxSearchSettings_OnOkClick, this.OnOkClick);
        this.RegisterOperation(LocalOperations.Labs.LabInboxSearchSettings_OnCancelClick, this.OnCancelClick);
        this.RegisterOperation(LocalOperations.Labs.LabInboxSearchSettings_OnResultsPerPageChange, this.OnResultsPerPageChange);
        this.RegisterOperation(LocalOperations.Labs.LabInboxSearchSettings_ShowOptionsButtonClick, this.ToggleSearchOptionsDropdown);
        this.RegisterOperation(LocalOperations.Labs.LabInboxColumnOrderingPopup_Open, this.ShowColumnOrderingPopup);
        this.RegisterOperation(LocalOperations.Labs.LabInboxColumnOrderingPopup_Close, this.CloseColumnOrderingPopup);
        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.OnContainerClick, false);
        this.RegisterOperation(LocalOperations.Labs.LabInboxSearchSettings_CheckboxChange, this.OnSettingsContainerCheckboxChange);
        this.RegisterOperation(LocalOperations.Labs.LabInboxSearchSettings_FromDateChanged, this.FromDateChanged);
        this.RegisterOperation(LocalOperations.Labs.LabInboxSearchSettings_ToDateChanged, this.ToDateChanged);
        this.RegisterOperation(LocalOperations.Labs.LabInboxSearchSettings_IamPrescriberCheckboxChange, this.IamPrescriberCheckboxChange);
        this.RegisterOperation(LocalOperations.Labs.LabInboxSearchSettings_IamCcCheckboxChange, this.IamCcCheckboxChange);
    }

    private IamPrescriberCheckboxChange(request: LocalEvent, store: LabsStore): void {
        var checked = request.Get<boolean>("checked");
        store.LabsInboxModel = store.LabsInboxModel.set("IamPrescriberSelected", checked);
    }

    private IamCcCheckboxChange(request: LocalEvent, store: LabsStore): void {
        var checked = request.Get<boolean>("checked");
        store.LabsInboxModel = store.LabsInboxModel.set("IamInCcSelected", checked);
    }

    private FromDateChanged(request: LocalEvent, store: LabsStore): void {
        store.LabsInboxModel = store.LabsInboxModel.set("DateFrom", request.Get<Date>("value"));
    }

    private ToDateChanged(request: LocalEvent, store: LabsStore): void {
        store.LabsInboxModel = store.LabsInboxModel.set("DateTo", request.Get<Date>("value"));
    }

    private OnSettingsContainerCheckboxChange(request: LocalEvent, store: LabsStore): void {
        const target = request.Get<CheckboxTarget>("target");
        const checked = request.Get<boolean>("checked");
        const id = request.Get<string>("id");
        const currentClinicianList = store.LabsInboxModel.get("CliniciansGlobalCache");
        const currentSentByList = store.LabsInboxModel.get("SentByGlobalCache");

        switch (target) {
            case CheckboxTarget.Myle_Documents_PortalStatusControlCheck:
                store.LabsInboxModel = store.LabsInboxModel.set("SelectAllPortalStatus", checked);
                var statuses = store.helper.ChangeCheckStateFromAllICheckable(store.LabsInboxModel.get("PortalStatuses"), checked);
                store.LabsInboxModel = store.LabsInboxModel.set("PortalStatuses", statuses);
                break;

            case CheckboxTarget.Myle_Labs_PortalStatus:
                if (!checked && store.LabsInboxModel.get("SelectAllPortalStatus")) { store.LabsInboxModel = store.LabsInboxModel.set("SelectAllPortalStatus", false); }
                store.LabsInboxModel = store.LabsInboxModel.set("PortalStatuses", store.helper.FindUpdateICheckableItem<PortalStatusCheckable>(store.LabsInboxModel.get("PortalStatuses"), id, checked));
                break;
            case CheckboxTarget.Myle_Documents_SentByControlCheck:
                store.LabsInboxModel = store.LabsInboxModel.set("SelectAllPortalSentBy", checked);
                var list = currentSentByList.map(c => {
                    if (c.Visible === true) { c.Checked = checked; } return c;
                }).toList();
                store.LabsInboxModel = store.LabsInboxModel.set("SentByGlobalCache", list);
                break;
            case CheckboxTarget.Myle_Labs_PortalSentBy:
                if (!checked && store.LabsInboxModel.get("SelectAllPortalSentBy")) { store.LabsInboxModel = store.LabsInboxModel.set("SelectAllPortalSentBy", false); }
                let updatedSentByList = currentSentByList.map(c => { if (c.Id === id) { c.Checked = checked; } return c; }).toList();
                store.LabsInboxModel = store.LabsInboxModel.set("SentByGlobalCache", updatedSentByList);
                break;
            case CheckboxTarget.Myle_Labs_Sites:
                store.LabsInboxModel = store.LabsInboxModel.set("Sites", store.helper.FindUpdateICheckableItem<SiteUIModel>(store.LabsInboxModel.get("Sites"), id, checked));
                store.LabsInboxModel = store.LabsInboxModel.set("CliniciansGlobalCache", store.UpdateVisibilityToCliniciansFromSelectedSites(currentClinicianList));
                store.LabsInboxModel = store.LabsInboxModel.set("SelectAllSitesChecked", store.LabsInboxModel.get("Sites").filter(s => s.Visible).every(s => s.Checked));
                break;
            case CheckboxTarget.Myle_Labs_Clinicians:
                let updatedList = currentClinicianList.map(c => { if (c.Id === id) { c.Checked = checked; } return c; }).toList();
                store.LabsInboxModel = store.LabsInboxModel.set("CliniciansGlobalCache", updatedList);
                store.LabsInboxModel = store.LabsInboxModel.set("SelectAllCliniciansChecked", updatedList.filter(p => p.Visible).every(p => p.Checked));
                break;
            case CheckboxTarget.Myle_Labs_SitesControlCheck:
                store.LabsInboxModel = store.LabsInboxModel.set("SelectAllSitesChecked", checked);
                var sites = store.helper.ChangeCheckStateFromAllICheckable(store.LabsInboxModel.get("Sites"), checked);
                store.LabsInboxModel = store.LabsInboxModel.set("Sites", sites);
                store.LabsInboxModel = store.LabsInboxModel.set("CliniciansGlobalCache", store.UpdateVisibilityToCliniciansFromSelectedSites(currentClinicianList));
                break;
            case CheckboxTarget.Myle_Labs_AllCliniciansPrescriptorControlCheck:
                store.LabsInboxModel = store.LabsInboxModel.set("SelectAllCliniciansChecked", checked);

                var list = currentClinicianList.map(c => {
                    if (c.Visible === true) { c.Checked = checked; } return c;
                }).toList();

                store.LabsInboxModel = store.LabsInboxModel.set("CliniciansGlobalCache", list);
                break;
            case CheckboxTarget.Myle_Labs_ShowDeactivatedClinicians:
                store.LabsInboxModel = store.LabsInboxModel.set("ShowDeactivatedClinicians", checked);
                store.LabsInboxModel = store.LabsInboxModel.set("CliniciansGlobalCache", store.UpdateVisibilityToCliniciansFromSelectedSites(currentClinicianList));
                break;
            case CheckboxTarget.Myle_Labs_SaveSettingsSelected:
                store.LabsInboxModel = store.LabsInboxModel.set("SaveSettingsSelected", checked);
                break;

            case CheckboxTarget.Myle_Labs_ShowAbnormalOnlysSelected:
                store.LabsInboxModel = store.LabsInboxModel.set("ShowAbnormalsOnly", checked);
                break;

            case CheckboxTarget.Myle_Labs_ShowEnrolledPatientsResults:
                store.LabsInboxModel = store.LabsInboxModel.set("ShowEnrolledPatientsResults", checked);
                break;

            case CheckboxTarget.Myle_Labs_HideResultsFiledByColleague:
                store.LabsInboxModel = store.LabsInboxModel.set("HideResultsFiledByColleague", checked);
                break;

            case CheckboxTarget.Myle_Labs_AlwaysShowAbnormalResults:
                store.LabsInboxModel = store.LabsInboxModel.set("AlwaysShowAbnormalResults", checked);
                break;

            case CheckboxTarget.Myle_Labs_ProcessingStatus_PendingCancelled:
            case CheckboxTarget.Myle_Labs_ProcessingStatus_Partial:
            case CheckboxTarget.Myle_Labs_ProcessingStatus_Preliminary:
            case CheckboxTarget.Myle_Labs_ProcessingStatus_Final:
            case CheckboxTarget.Myle_Labs_ProcessingStatus_Others: {
                const currentList = store.LabsInboxModel.get("SelectedLabProcessingStatuses") ?? Immutable.List<LabProcessingStatusFilterOption>();
                const resolvedStatus = this.getLabProcessingStatusFromCheckboxTarget(target);

                if (checked)
                    store.LabsInboxModel = store.LabsInboxModel.set("SelectedLabProcessingStatuses", currentList.push(resolvedStatus));
                else
                    store.LabsInboxModel = store.LabsInboxModel.set("SelectedLabProcessingStatuses", currentList.filter(p => p !== resolvedStatus).toList());
                break;
            }
        }
    }

    private ShowColumnOrderingPopup(request: LocalEvent, store: LabsStore): void {
        store.LabsInboxModel = store.LabsInboxModel.set("ColumnOrderingPopupOpen", true);
        store.LabsInboxModel = store.LabsInboxModel.set("LabInboxDisplaySettingsOption", false);
        sessionStore.showGlobalOverlay(true);
    }

    private CloseColumnOrderingPopup(request: LocalEvent, store: LabsStore): void {
        store.LabsInboxModel = store.LabsInboxModel.set("ColumnOrderingPopupOpen", false);
        store.LabsInboxModel = store.LabsInboxModel.set("LabInboxDisplaySettingsOption", false);
        sessionStore.hideGlobalOverlay();
    }

    private ClinicianFilterChanged(request: LocalEvent, store: LabsStore): void {
        store.LabsInboxModel = store.LabsInboxModel.set("CliniciansSearchText", request.Get<string>("value"));
    }

    private SentByFilterChanged(request: LocalEvent, store: LabsStore): void {
        store.LabsInboxModel = store.LabsInboxModel.set("SentBySearchText", request.Get<string>("value"));
    }

    private OnOkClick(request: LocalEvent, store: LabsStore): void {
        this.SaveLabSetting(store);
        store.LabsInboxModel = store.LabsInboxModel.set("LabInboxDisplaySettingsOption", false);
        labsStore.LabActions.RefreshLabs();
        store.LabActions.GetNewLabsCount();
    }

    private OnCancelClick(request: LocalEvent, store: LabsStore): void {
        store.LabsInboxModel = store.LabsInboxModel.set("LabInboxDisplaySettingsOption", false);
    }

    private OnResultsPerPageChange(request: LocalEvent, store: LabsStore): void {
        store.LabsInboxModel = store.LabsInboxModel
            .set("SelectedPageNumber", 1)
            .set("SelectedPageSizeOption", request.Get<number>("value"));
    }

    private ToggleSearchOptionsDropdown(request: LocalEvent, store: LabsStore): void {
        var open = store.LabsInboxModel.get("LabInboxDisplaySettingsOption");
        store.LabsInboxModel = store.LabsInboxModel.set("LabInboxDisplaySettingsOption", !open);

        if (!store.LabsInboxModel.get("LabInboxDisplaySettingsOption")) {
            window.setTimeout(() => LocalEvent.Dispatch(LocalOperations.Labs.OnRefreshClick), 20);
        }
    }

    private OnContainerClick(request: LocalEvent, store: LabsStore): void {
        if (store.LabsInboxModel.get("LabInboxDisplaySettingsOption") === true) {
            this.SaveLabSetting(store);
            store.LabsInboxModel = store.LabsInboxModel.set("LabInboxDisplaySettingsOption", false);
            labsStore.LabActions.RefreshLabs();
            store.EmitChangeToListeners();
        }
    }

    private SaveLabSetting(store: LabsStore): void {
        if (!store.LabsInboxModel.get("IsFromPatient"))
            store.counterSettingsHideResultsFiledByColleague = store.LabsInboxModel.get("HideResultsFiledByColleague");

        if (!store.LabsInboxModel.get("SaveSettingsSelected"))
            return;

        const inboxModel = store.LabsInboxModel;
        const labSettings: LabSettings = {
            IsAllSitesChecked: inboxModel.get("SelectAllSitesChecked"),
            IsAllCliniciansChecked: inboxModel.get("SelectAllCliniciansChecked"),
            IsDeactivatedChecked: inboxModel.get("ShowDeactivatedClinicians"),
            IsAllPortalSentByChecked: inboxModel.get("SelectAllPortalSentBy"),
            IsAllPortalStatusChecked: inboxModel.get("SelectAllPortalStatus"),
            SiteIds: inboxModel
                .get("Sites")
                .filter(p => p.Checked)
                .map(p => p.Model.Id)
                .toArray(),
            EmployeeIds: inboxModel
                .get("CliniciansGlobalCache")
                .filter(p => p.Checked)
                .map(p => p.Model.ContactFacet_id)
                .toArray(),
            PortalSentByIds: inboxModel
                .get("SentByGlobalCache")
                .filter(p => p.Checked)
                .map(p => p.Model.ContactFacet_id)
                .toArray(),
            PortalStatusesIds: inboxModel
                .get("PortalStatuses")
                .filter(p => p.Checked)
                .map(p => p.Id)
                .toArray(),
            IsOnlyAbnormalResults: inboxModel.get("ShowAbnormalsOnly"),
            IsShowEnrolledPatientsResults: inboxModel.get("ShowEnrolledPatientsResults"),
            HideResultsFiledByColleagueChecked: inboxModel.get("HideResultsFiledByColleague"),
            PageSize: inboxModel.get("SelectedPageSizeOption"),
            AlwaysShowAbnormalResults: inboxModel.get("AlwaysShowAbnormalResults"),
            SelectedLabProcessingStatuses: inboxModel.get("SelectedLabProcessingStatuses").toArray()
        };

        // save
        store.LabActions.SaveLabSettings(sessionStore.userId, labSettings);
        store.LabsInboxModel = inboxModel.set("SaveSettingsSelected", false);
    }

    private getLabProcessingStatusFromCheckboxTarget(target: CheckboxTarget): LabProcessingStatusFilterOption {
        switch (target) {
            case CheckboxTarget.Myle_Labs_ProcessingStatus_PendingCancelled:
                return LabProcessingStatusFilterOption.PendingCancelled;
            case CheckboxTarget.Myle_Labs_ProcessingStatus_Partial:
                return LabProcessingStatusFilterOption.Partial;
            case CheckboxTarget.Myle_Labs_ProcessingStatus_Preliminary:
                return LabProcessingStatusFilterOption.Preliminary;
            case CheckboxTarget.Myle_Labs_ProcessingStatus_Final:
                return LabProcessingStatusFilterOption.Final;
            default:
                return LabProcessingStatusFilterOption.Other;
        }
    }
}

export { LabsInboxSettingsContainerHandler };
